import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 1000, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const inputStyle = { width: '100%', padding: 10, marginBottom: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 };
const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 14 };
const btnPrimary = { padding: '10px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const API_BASE = process.env.REACT_APP_API_BASE || '/api';

// ✅ CLIENT-SIDE IMAGE COMPRESSOR
const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > maxWidth) { height *= maxWidth / width; width = maxWidth; }
        } else {
          if (height > maxHeight) { width *= maxHeight / height; height = maxHeight; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) { reject(new Error('Canvas is empty')); return; }
            const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '') + '.jpg', { type: 'image/jpeg' });
            resolve(compressedFile);
          }, 'image/jpeg', quality
        );
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

export default function MathQuestionUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [level, setLevel] = useState('1');
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '', details: null });
  
  const [questionsNeedingImages, setQuestionsNeedingImages] = useState([]);
  const [imageUploadStatus, setImageUploadStatus] = useState({});

  // ✅ NEW: State for Duplicate Modal
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [duplicateList, setDuplicateList] = useState([]);
  const [showDuplicateList, setShowDuplicateList] = useState(false);
  const [pendingData, setPendingData] = useState(null);

  const handleFileChange = (e) => setFile(e.target.files[0]);

  const handleUploadCSV = async (e, forceReplace = false) => {
    if (e) e.preventDefault();
    if (!file || !level) { setStatus({ type: 'error', message: 'Please select a CSV file and choose a level.' }); return; }

    setUploading(true);
    setStatus({ type: '', message: '', details: null });
    
    const formData = new FormData();
    formData.append('csvFile', file);
    formData.append('level', level);
    if (forceReplace) formData.append('replaceDuplicates', 'true');

    try {
      const res = await fetch(`${API_BASE}/admin/upload/math-questions`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      
      // ✅ If duplicates found and we haven't asked yet and not forcing replace
      if (data.duplicates_found > 0 && !forceReplace) {
          setDuplicateCount(data.duplicates_found);
          setDuplicateList(data.duplicate_questions || []);
          setPendingData(data);
          setShowDuplicateModal(true);
          setShowDuplicateList(false);
          setUploading(false);
          return;
      }

      // If we are here, either no duplicates, or user made a choice
      let message = `✅ Processed ${data.total_processed} questions:`;
      if (data.inserted > 0) message += ` ${data.inserted} new added`;
      if (data.updated > 0) message += `, ${data.updated} updated`;
      if (data.duplicates_skipped > 0) message += `, ${data.duplicates_skipped} skipped`;
      if (data.errors && data.errors.length > 0) message += `, ${data.errors.length} errors`;
      
      setStatus({ type: 'success', message, details: data });
      setShowDuplicateModal(false);
      setPendingData(null);
      
      if (data.questions_needing_images && data.questions_needing_images.length > 0) {
        setQuestionsNeedingImages(data.questions_needing_images);
      } else {
        setFile(null);
      }
    } catch (err) {
      setStatus({ type: 'error', message: `❌ ${err.message}` });
    } finally {
      setUploading(false);
    }
  };

  // ✅ NEW: Modal Handlers
  const handleReplaceDuplicates = () => {
      setShowDuplicateModal(false);
      handleUploadCSV(null, true);
  };

  const handleSkipDuplicates = () => {
      setShowDuplicateModal(false);
      const data = pendingData;
      let message = `✅ Processed ${data.total_processed} questions:`;
      if (data.inserted > 0) message += ` ${data.inserted} new added`;
      if (data.duplicates_skipped > 0) message += `, ${data.duplicates_skipped} skipped`;
      if (data.errors && data.errors.length > 0) message += `, ${data.errors.length} errors`;
      
      setStatus({ type: 'success', message, details: data });
      setPendingData(null);
      
      if (data.questions_needing_images && data.questions_needing_images.length > 0) {
        setQuestionsNeedingImages(data.questions_needing_images);
      } else {
        setFile(null);
      }
  };

  const handleImageUploadForQuestion = async (questionId, file) => {
    if (!file) return;
    try {
      setImageUploadStatus(prev => ({ ...prev, [questionId]: 'compressing' }));
      const compressedFile = await compressImage(file, 1200, 1200, 0.8);
      setImageUploadStatus(prev => ({ ...prev, [questionId]: 'uploading' }));
      
      const formData = new FormData();
      formData.append('imageFile', compressedFile);
      formData.append('questionId', questionId);

      const res = await fetch(`${API_BASE}/admin/upload/math-question-image`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      
      setImageUploadStatus(prev => ({ ...prev, [questionId]: 'success' }));
    } catch (err) {
      console.error(err);
      setImageUploadStatus(prev => ({ ...prev, [questionId]: 'error' }));
    }
  };

  return (
    <div style={containerStyle}>
      {/* ✅ Duplicate Confirmation Modal with View List */}
      {showDuplicateModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', padding: 30, borderRadius: 10, maxWidth: 700, width: '90%', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
            <h3 style={{ marginTop: 0, color: '#dc3545' }}>⚠️ Duplicates Detected</h3>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: '#333' }}>
              The system found <strong>{duplicateCount}</strong> question(s) that already exist in the database for this level.
            </p>
            
            {/* ✅ Toggle Button to Show/Hide Duplicate List */}
            <button 
              onClick={() => setShowDuplicateList(!showDuplicateList)}
              style={{ 
                padding: '8px 16px', 
                background: '#17a2b8', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 6, 
                cursor: 'pointer', 
                fontWeight: 600,
                marginBottom: 15,
                fontSize: 13
              }}
            >
              {showDuplicateList ? '🔽 Hide Duplicate Questions' : '👁️ View Duplicate Questions'}
            </button>

            {/* ✅ Duplicate List Display */}
            {showDuplicateList && duplicateList.length > 0 && (
              <div style={{ 
                background: '#fff3cd', 
                border: '1px solid #ffc107', 
                borderRadius: 6, 
                padding: 15, 
                marginBottom: 20,
                maxHeight: 300,
                overflowY: 'auto'
              }}>
                <h4 style={{ marginTop: 0, marginBottom: 10, color: '#856404' }}>Duplicate Questions:</h4>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {duplicateList.map((q, idx) => (
                    <li key={idx} style={{ marginBottom: 8, fontSize: 13, color: '#856404' }}>
                      <strong>Row {q.row}:</strong> {q.question_text.length > 100 ? q.question_text.substring(0, 100) + '...' : q.question_text}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p style={{ fontSize: 14, color: '#666', marginBottom: 25 }}>
              How would you like to handle them?
            </p>
            <div style={{ display: 'flex', gap: 15, justifyContent: 'flex-end' }}>
              <button onClick={handleSkipDuplicates} style={{ padding: '10px 20px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                Skip Duplicates
              </button>
              <button onClick={handleReplaceDuplicates} style={{ padding: '10px 20px', background: '#ffc107', color: '#000', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                Replace Originals
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>📤 Upload Math Questions</h2>
        <button onClick={() => navigate('/admin/dashboard')} style={btnSecondary}>← Back</button>
      </div>

      {status.message && (
        <div style={{ padding: 12, background: status.type === 'success' ? '#d4edda' : '#f8d7da', color: status.type === 'success' ? '#155724' : '#721c24', borderRadius: 6, marginBottom: 15 }}>
          <strong>{status.message}</strong>
          {status.details?.errors?.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: 8 }}>
              <strong>Errors:</strong>
              <ul style={{ margin: '5px 0 0 20px', padding: 0 }}>
                {status.details.errors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}> Step 1: Upload CSV</h3>
        <form onSubmit={handleUploadCSV}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 15, marginBottom: 15 }}>
            <div>
              <label style={labelStyle}>Target Level *</label>
              <select style={inputStyle} value={level} onChange={e => setLevel(e.target.value)}>
                {Array.from({ length: 12 }, (_, i) => (i + 1).toString()).map(l => <option key={l} value={l}>Level {l}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>CSV File *</label>
              <input type="file" accept=".csv" onChange={handleFileChange} style={{ ...inputStyle, padding: 8 }} ref={fileInputRef} />
            </div>
          </div>

          <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 6, fontSize: 13, color: '#555', lineHeight: 1.5, marginBottom: 15 }}>
            <strong>Required CSV Columns:</strong> <code>chapter, chapter_name, concept, difficulty, concept_type, question_text, answer_a/b/c/d, correct_answer</code><br/>
            <strong>For Images:</strong> Add a <code>needs_image</code> column. Put <code>Yes</code> if the question has an image. You will upload the image in Step 2.
          </div>

          <button type="submit" disabled={uploading || !file} style={{ ...btnPrimary, opacity: (uploading || !file) ? 0.6 : 1 }}>
            {uploading ? ' Processing...' : '🚀 Upload Questions'}
          </button>
        </form>
      </div>

      {questionsNeedingImages.length > 0 && (
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '2px solid #007BFF', marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, color: '#007BFF' }}>📸 Step 2: Attach Images to Questions</h3>
          <p style={{ color: '#666', fontSize: 14, marginBottom: 20 }}>
            The system detected <strong>{questionsNeedingImages.length}</strong> questions that need images. Images will be automatically compressed before uploading.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            {questionsNeedingImages.map((q) => {
              const status = imageUploadStatus[q.id];
              return (
                <div key={q.id} style={{ padding: 15, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
                  <div style={{ marginBottom: 10, fontSize: 15 }}>
                    <strong>Question:</strong> {q.question_text.length > 80 ? q.question_text.substring(0, 80) + '...' : q.question_text}
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleImageUploadForQuestion(q.id, e.target.files[0])}
                      disabled={status === 'success' || status === 'uploading' || status === 'compressing'}
                      style={{ flex: 1 }}
                    />
                    
                    {status === 'compressing' && <span style={{ color: '#fd7e14', fontWeight: 600 }}>⚙️ Compressing...</span>}
                    {status === 'uploading' && <span style={{ color: '#007BFF', fontWeight: 600 }}>⏳ Uploading...</span>}
                    {status === 'success' && <span style={{ color: '#28a745', fontWeight: 600 }}>✅ Attached!</span>}
                    {status === 'error' && <span style={{ color: '#dc3545', fontWeight: 600 }}>❌ Failed</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 20, padding: 15, background: '#e7f1ff', borderRadius: 6, textAlign: 'center' }}>
            <button 
              onClick={() => { setQuestionsNeedingImages([]); setFile(null); }} 
              style={{ ...btnPrimary, background: '#007BFF' }}
            >
              ✅ All Done! Finish Upload
            </button>
          </div>
        </div>
      )}
    </div>
  );
}